<template>
  <div id="table" class="overflow-hidden rounded-xl">
    <div class="hidden lg:block">
      <table class="min-w-full">
        <!-- Table Head -->
        <thead class="bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
          <tr>
            <!-- Select All Checkbox -->
            <th
              v-if="selectable"
              class="px-6 py-4 text-left text-xs font-medium text-slate-400 dark:text-slate-400 uppercase tracking-wider"
            >
              <slot name="header-select">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleAllSelection"
                  class="h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                />
              </slot>
            </th>
            <!-- Column Headers -->
            <th
              v-for="column in columns"
              :key="column.key"
              @click="column.sortable ? sort(column.key) : null"
              :class="[
                'px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider select-none',
                column.sortable ? 'cursor-pointer hover:text-slate-300 dark:hover:text-slate-300 transition-colors' : '',
              ]"
            >
              <div class="flex items-center">
                <!-- check if parent passed a custom header slot -->
                <slot :name="'head(' + column.key + ')'" :column="column">
                  {{ column.label }}
                </slot>

                <!-- keep sort icon if sortable -->
                <svg
                  v-if="column.sortable"
                  class="h-4 w-4 ml-1 text-slate-400 dark:text-slate-400 transition-transform"
                  xmlns="http://www.w3.org/2000/svg"
                  :class="{
                    'transform rotate-180': sortKey === column.key && sortOrder === 'desc',
                  }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M10 3a1 1 0 01.894.553l5 10A1 1 0 0115 15H5a1 1 0 01-.894-1.447l5-10A1 1 0 0110 3z"
                  />
                </svg>
              </div>
            </th>
          </tr>
        </thead>
        <!-- Table Body -->
        <tbody class="bg-white/80 dark:bg-slate-900/60 backdrop-blur-md">
          <tr
            v-for="(item, index) in sortedItems"
            :key="item[itemKey]"
            @click="handleRowClick(item)"
            :class="[
              'transition-all duration-200 cursor-pointer border-b border-gray-200/50 dark:border-slate-800/50 last:border-0',
              isSelected(item)
                ? 'bg-blue-500/20 hover:bg-blue-500/30 dark:bg-blue-500/20 dark:hover:bg-blue-500/30'
                : item?.marked_for_deletion || item?.differences?.marked_for_deletion || item?.last_update_status === 'failed'
                  ? 'bg-red-500/20 hover:bg-red-500/30 dark:bg-red-500/20 dark:hover:bg-red-500/30'
                  : item?.pending || item.license_state === 'Unapproved' || item.status === 'pending'
                    ? 'bg-yellow-500/20 hover:bg-yellow-500/30 dark:bg-yellow-500/20 dark:hover:bg-yellow-500/30'
                    : 'hover:bg-gray-100/60 dark:hover:bg-slate-800/60'
            ]"
          >
            <!-- Item Checkbox -->
            <td v-if="selectable" class="px-6 py-4">
              <slot name="cell-select" :item="item">
                <input
                  type="checkbox"
                  :checked="isSelected(item)"
                  @change="toggleSelection(item, $event)"
                  @click.stop
                  class="h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                />
              </slot>
            </td>
            <!-- Item Data -->
            <td
              v-for="column in columns"
              :key="column.key"
              class="px-6 py-4 text-sm text-gray-900 dark:text-slate-200 font-normal break-all"
            >
              <slot :name="column.key" :item="item" :value="item[column.key]">
                <template v-if="column.key === 'pending'">
                  <span
                    v-if="item[column.key]"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-500/80 text-white dark:text-slate-900"
                  >
                    Pending
                  </span>
                  <span v-else>-</span>
                </template>
                <template v-else>
                  {{ item[column.key] }}
                </template>
              </slot>
            </td>
          </tr>
          <!-- Empty State -->
          <tr v-if="sortedItems.length === 0">
            <td
              :colspan="selectable ? columns.length + 1 : columns.length"
              class="px-6 py-8 text-center text-gray-500 dark:text-slate-400"
            >
              <div class="flex flex-col items-center space-y-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p class="font-medium">No items to display</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="block lg:hidden">
      <div class="space-y-6 p-4">
        <div
          v-if="sortedItems.length === 0"
          class="bg-white/80 dark:bg-slate-900/60 backdrop-blur-md p-8 text-center text-gray-500 dark:text-slate-400 rounded-xl border border-gray-200/50 dark:border-slate-800/50"
        >
          <div class="flex flex-col items-center space-y-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p class="font-medium">No items to display</p>
          </div>
        </div>
        <div
          v-for="(item, index) in sortedItems"
          :key="item[itemKey]"
          @click="handleRowClick(item)"
          class="bg-white/80 dark:bg-slate-900/60 backdrop-blur-md rounded-xl overflow-hidden transition-all duration-300 hover:bg-gray-50/90 dark:hover:bg-slate-800/70 cursor-pointer border border-gray-200/50 dark:border-slate-800/50"
          :class="{
            'ring-2 ring-blue-500 border-blue-500/40': isSelected(item),
            'border-yellow-500/40 bg-yellow-500/10': item.pending,
            'border-red-500/40 bg-red-500/10': item?.last_update_status === 'failed',
          }"
        >
          <!-- Card Header -->
          <div class="p-5 border-b border-gray-200/50 dark:border-slate-800/50 bg-gradient-to-r from-gray-100/60 dark:from-slate-800/60 to-transparent">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <input
                  v-if="selectable"
                  type="checkbox"
                  :checked="isSelected(item)"
                  @change="toggleSelection(item, $event)"
                  @click.stop
                  class="h-5 w-5 text-blue-500 focus:ring-blue-500 border-gray-300 dark:border-slate-600 rounded cursor-pointer"
                />
                <h3 class="font-semibold text-gray-900 dark:text-slate-200 text-lg">
                  {{ item.name }}
                </h3>
              </div>
              <div
                v-if="item.pending"
                class="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-yellow-500/80 text-white dark:text-slate-900"
              >
                Pending
              </div>
            </div>
          </div>

          <!-- Card Content -->
          <div class="divide-y divide-gray-200/50 dark:divide-slate-800/50">
            <div
              v-for="column in columns.filter((col) => col.key !== 'name')"
              :key="column.key"
              class="flex justify-between items-start p-4 hover:bg-gray-100/30 dark:hover:bg-slate-800/30 transition-colors"
            >
              <span class="text-sm font-medium text-gray-600 dark:text-slate-400">
                {{ column.label }}
              </span>
              <div class="text-sm text-gray-900 dark:text-slate-200 text-right font-normal">
                <slot :name="column.key" :item="item" :value="item[column.key]">
                  <template v-if="column.key === 'pending'">
                    <span v-if="item[column.key]">Pending</span>
                    <span v-else>-</span>
                  </template>
                  <template v-else>
                    {{ item[column.key] }}
                  </template>
                </slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GenericTable",
  props: {
    columns: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    itemKey: {
      type: String,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    persistSelections: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue", "update:selectedItems", "rowClick", "sort"],
  data() {
    return {
      selectedItems: this.modelValue || [],
      sortKey: "",
      sortOrder: "asc",
    };
  },
  watch: {
    modelValue: {
      handler(newValue) {
        this.selectedItems = newValue;
      },
      immediate: true,
    },
  },
  computed: {
    allSelected() {
      return (
        this.items.length > 0 &&
        this.items.every((item) => this.isSelected(item))
      );
    },
    sortedItems() {
      if (!this.sortKey) return this.items;

      return [...this.items].sort((a, b) => {
        const aValue = a[this.sortKey];
        const bValue = b[this.sortKey];

        if (aValue < bValue) return this.sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    },
  },
  methods: {
    clearSelection() {
      this.selectedItems = []
    },
    isSelected(item) {
      return this.selectedItems.some(
        (selected) => selected[this.itemKey] === item[this.itemKey]
      );
    },
    toggleSelection(item, event) {
      const index = this.selectedItems.findIndex(
        (selected) => selected[this.itemKey] === item[this.itemKey]
      );
      const newSelection = [...this.selectedItems];

      if (index === -1) {
        newSelection.push(item);
      } else {
        newSelection.splice(index, 1);
      }

      this.selectedItems = newSelection;
      this.$emit("update:modelValue", this.selectedItems);
      this.$emit("update:selectedItems", this.selectedItems);
    },
    toggleAllSelection() {
      if (this.allSelected) {
        this.selectedItems = [];
      } else {
        this.selectedItems = [...this.sortedItems];
      }
      this.$emit("update:modelValue", this.selectedItems);
      this.$emit("update:selectedItems", this.selectedItems);
    },
    sort(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
      this.$emit("sort", { key, order: this.sortOrder });
    },
    handleRowClick(item) {
      this.$emit("rowClick", item);
    },
  },
};
</script>
